import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * PrimaryButton  Global CTA button for the entire site.
 *
 * Props:
 *  - children:    Button text
 *  - onClick:     Click handler (renders <button>)
 *  - to:          Route path (renders <Link>)
 *  - href:        External URL (renders <a>)
 *  - type:        "button" | "submit" (default: "button")
 *  - icon:        Lucide icon component for right side (default: ChevronRight, pass null to hide)
 *  - disabled:    Disable button
 *  - className:   Additional classes
 *  - fullWidth:   Stretch to 100%
 */
const PrimaryButton = ({
  children,
  onClick,
  to,
  href,
  type = "button",
  icon: IconComponent = ChevronRight,
  disabled = false,
  className = "",
  fullWidth = false,
  ...rest
}) => {
  const baseClasses = `
        relative group inline-flex items-center justify-center gap-2
        bg-white text-[#0b1121] font-extrabold text-sm
        px-6 sm:px-9 py-3.5 rounded-full
        overflow-hidden
        transition-all duration-500
        ${fullWidth ? "w-full" : ""}
        ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:scale-[1.03] active:scale-[0.98]"}
        ${className}
    `
    .trim()
    .replace(/\s+/g, " ");

  const innerContent = (
    <>
      {/* Gradient overlay  visible on hover */}
      <div className="absolute inset-0 bg-linear-to-r from-brand-blue via-[#7c5bf6] to-brand-purple opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0 rounded-full" />
      {/* Internal radial glow on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-30 bg-[radial-gradient(ellipse_at_center,white_0%,transparent_70%)] transition-opacity duration-500 z-0 rounded-full" />
      {/* Outer glow shadow on hover */}
      <div className="absolute -inset-[2px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_25px_rgba(59,130,246,0.5),0_0_50px_rgba(139,92,246,0.3)] pointer-events-none z-0" />
      {/* Text layer */}
      <span className="relative z-10 group-hover:text-white transition-colors duration-500 flex items-center justify-center text-center gap-2">
        {children}
        {IconComponent && (
          <IconComponent
            size={16}
            className="group-hover:translate-x-1 transition-transform duration-300 shrink-0"
          />
        )}
      </span>
    </>
  );

  // Render as <Link> for internal navigation
  if (to) {
    return (
      <Link to={to} className={baseClasses} {...rest}>
        {innerContent}
      </Link>
    );
  }

  // Render as <a> for external URLs
  if (href) {
    return (
      <a
        href={href}
        className={baseClasses}
        target="_blank"
        rel="noopener noreferrer"
        {...rest}
      >
        {innerContent}
      </a>
    );
  }

  // Render as <button> for actions and form submissions
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={baseClasses}
      {...rest}
    >
      {innerContent}
    </button>
  );
};

export default PrimaryButton;
